import { ReactElement, useEffect, useState } from 'react'
import { useMutation, useQuery, NetworkStatus } from '@apollo/client'
import { FormattedMessage } from 'react-intl'
import { isEmpty } from 'lodash'

import Card from '@10xLabs/react-web/components/Card'
import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import { InsuranceSettingsForm } from './InsuranceSettingsForm'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { canWrite } from 'features/Users/utils'
import { sanitizePrice } from 'utils/math'
import { getDifference } from 'utils/object'
import { IInsuranceSettings, IInsuranceSettingsCard } from './types'
import { useLogin } from 'features/Login/hooks'
import { GET_COMPANY_INSURANCE_SETTINGS } from 'features/Ancillaries/queries-legacy'
import { CHANGE_COMPANY_INSURANCE_SETTINGS } from 'features/Ancillaries/mutations-legacy'

export const InsuranceSettingsCard = ({ company = { id: '' } }: IInsuranceSettingsCard): ReactElement => {
  const login = useLogin()
  const [savedSettings, setSavedSettings] = useState()

  const {
    loading: queryLoading,
    error: queryError,
    data: queryData,
    refetch,
    networkStatus
  } = useQuery(GET_COMPANY_INSURANCE_SETTINGS, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    skip: !company.id,
    variables: {
      companyId: company.id
    }
  })
  const [changeCompanyInsuranceSettings, { loading: mutationLoading, error: mutationError }] = useMutation(
    CHANGE_COMPANY_INSURANCE_SETTINGS
  )

  const handleSubmit = async (settings: IInsuranceSettings): Promise<void> => {
    const previousData = savedSettings || queryData.companyInsuranceSettings
    const previousSettings = {
      insuranceSettings: previousData
    }

    const newSettings = {
      insuranceSettings: {
        active: settings.active,
        price: sanitizePrice(settings.price * 100, true),
        salesChannelIDs: settings.salesChannels.map(({ id }) => id)
      }
    }

    const payload = getDifference(newSettings, previousSettings) as { insuranceSettings: Partial<IInsuranceSettings> }

    if (!isEmpty(payload)) {
      try {
        await changeCompanyInsuranceSettings({
          variables: {
            settings: {
              companyID: company.id,
              ...payload
            }
          }
        })
        setSavedSettings({
          ...previousSettings.insuranceSettings,
          ...payload.insuranceSettings
        })
      } catch (ex) {
        /* empty */
      }
    }
  }

  useEffect((): TSFixMe => {
    refetch()
  }, [])

  useEffect((): TSFixMe => {
    setSavedSettings(undefined)
  }, [company?.id])

  if (!company.id) {
    return (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="ancillaries.settings.insurance.edit.emptyCompany.description"
              defaultMessage="Select first your company"
            />
          </EmptyState.Description>
        </EmptyState.Container>
      </Card.Content>
    )
  } else if (networkStatus === NetworkStatus.refetch || queryLoading) {
    return (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <Loader />
        </EmptyState.Container>
      </Card.Content>
    )
  } else if (queryError) {
    return (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="ancillaries.settings.insurance.edit.error.description"
              defaultMessage="There was an error while loading your insurance settings"
            />
          </EmptyState.Description>

          <EmptyState.Action>
            <Button
              onClick={(): void => {
                refetch()
              }}
              kind="primary"
            >
              <FormattedMessage
                id="ancillaries.settings.insurance.edit.error.action"
                defaultMessage="Reload your insurance settings"
              />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      </Card.Content>
    )
  } else {
    return (
      <InsuranceSettingsForm
        onSubmit={handleSubmit}
        settings={savedSettings || queryData.companyInsuranceSettings}
        loading={mutationLoading}
        error={mutationError}
        disabled={!canWrite(FEATURES.ANCILLARIES, login.accessToken.policies)}
      />
    )
  }
}
